import React from 'react';

const CostComparisonTable = ({ 
  title = "Staffing Cost Advantage",
  subtitle = "Our offshore staffing reduces your development costs by 60-65% while delivering the same expertise and quality",
  showBadge = true,
  className = "",
  customData = null
}) => {
  // Add CSS animation for floating particles
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes float {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        50% { transform: translateY(-20px) rotate(180deg); }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const defaultCostData = [
    {
      role: 'Senior AI/ML Engineer',
      us: '$150,000',
      uk: '£80,000',
      offshore: '$55,000',
      savings: '60-65%'
    },
    {
      role: 'Data Scientist',
      us: '$130,000',
      uk: '£70,000',
      offshore: '$48,000',
      savings: '60-65%'
    },
    {
      role: 'ML Engineer',
      us: '$140,000',
      uk: '£75,000',
      offshore: '$52,000',
      savings: '60-65%'
    },
    {
      role: 'Full Stack Developer',
      us: '$120,000',
      uk: '£65,000',
      offshore: '$45,000',
      savings: '60-65%'
    }
  ];

  const costData = customData || defaultCostData;

  return (
    <section className={`cost-comparison-section py-5 ${className}`} style={{
      background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.06) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(56, 189, 248, 0.04) 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, rgba(16, 185, 129, 0.03) 0%, transparent 50%)
        `,
        zIndex: 1
      }}></div>

      {/* Floating Particles */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2,
        pointerEvents: 'none'
      }}>
        {[...Array(6)].map((_, i) => (
          <div key={i} style={{
            position: 'absolute',
            width: '4px',
            height: '4px',
            background: 'rgba(14, 165, 233, 0.3)',
            borderRadius: '50%',
            top: `${15 + i * 15}%`,
            left: `${10 + i * 12}%`,
            animation: `float ${4 + i * 0.3}s ease-in-out infinite`,
            animationDelay: `${i * 0.4}s`
          }}></div>
        ))}
      </div>

      <div className="container" style={{ position: 'relative', zIndex: 3, maxWidth: '1600px' }}>
        <div className="row justify-content-center">
          <div className="col-12" style={{ padding: '0 1rem' }}>
            <div style={{
              background: '#ffffff',
              borderRadius: 'clamp(16px, 4vw, 24px)',
              padding: 'clamp(2rem, 6vw, 4rem) clamp(1.5rem, 4vw, 3rem)',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.05)',
              border: '2px solid rgba(14, 165, 233, 0.4)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Background Pattern */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `
                  radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.04) 0%, transparent 50%),
                  radial-gradient(circle at 80% 20%, rgba(56, 189, 248, 0.03) 0%, transparent 50%),
                  radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.02) 0%, transparent 50%)
                `,
                zIndex: 1
              }}></div>

              <div style={{ position: 'relative', zIndex: 2 }}>
                <div className="text-center mb-5">
                  <h3 style={{
                    fontSize: 'clamp(2rem, 4vw, 2.8rem)',
                    fontWeight: '800',
                    color: '#334155',
                    marginBottom: '1rem',
                    lineHeight: '1.2'
                  }}>
                    {title.includes('Cost Advantage') ? (
                      <>
                        <span style={{ color: '#334155' }}>Staffing</span>
                        <span style={{ 
                          background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text'
                        }}> Cost Advantage</span>
                      </>
                    ) : (
                      title
                    )}
                  </h3>
                  <p style={{
                    fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                    color: '#64748b',
                    maxWidth: '800px',
                    margin: '0 auto',
                    lineHeight: '1.6'
                  }}>
                    {subtitle.includes('60-65%') ? (
                      <>
                        Our offshore staffing reduces your development costs by <strong style={{ color: '#10b981' }}>60-65%</strong> while delivering the same expertise and quality
                      </>
                    ) : (
                      subtitle
                    )}
                  </p>
                </div>

                {/* Desktop Table View */}
                <div className="d-none d-lg-block" style={{
                  background: '#ffffff',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.05)',
                  border: '2px solid rgba(14, 165, 233, 0.4)',
                  margin: '0 auto',
                  maxWidth: '1400px'
                }}>
                  <div style={{
                    overflowX: 'auto',
                    borderRadius: '20px'
                  }}>
                    <table className="staffing-cost-table" style={{
                      width: '100%',
                      minWidth: '800px',
                      borderCollapse: 'separate',
                      borderSpacing: 0,
                      background: '#ffffff'
                    }}>
                      <thead>
                        <tr>
                          <th style={{
                            padding: '2rem 2rem 1.5rem 2rem',
                            textAlign: 'left',
                            fontWeight: '700',
                            fontSize: '0.9rem',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                            color: 'white',
                            border: 'none',
                            position: 'relative',
                            minWidth: '200px'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}>
                              <i className="bi bi-cpu" style={{ fontSize: '1rem' }}></i>
                              Role
                            </div>
                          </th>
                          <th style={{
                            padding: '2rem 1.5rem 1.5rem 1.5rem',
                            textAlign: 'center',
                            fontWeight: '700',
                            fontSize: '0.9rem',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                            color: 'white',
                            border: 'none',
                            minWidth: '150px'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.5rem'
                            }}>
                              <i className="bi bi-flag" style={{ fontSize: '1rem' }}></i>
                              US (Annual)
                            </div>
                          </th>
                          <th style={{
                            padding: '2rem 1.5rem 1.5rem 1.5rem',
                            textAlign: 'center',
                            fontWeight: '700',
                            fontSize: '0.9rem',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                            color: 'white',
                            border: 'none',
                            minWidth: '150px'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.5rem'
                            }}>
                              <i className="bi bi-flag" style={{ fontSize: '1rem' }}></i>
                              UK (Annual)
                            </div>
                          </th>
                          <th style={{
                            padding: '1.5rem 1.5rem 1.25rem 1.5rem',
                            textAlign: 'center',
                            fontWeight: '700',
                            fontSize: '0.85rem',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                            color: 'white',
                            border: 'none',
                            position: 'relative'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.5rem'
                            }}>
                              <i className="bi bi-star" style={{ fontSize: '1rem' }}></i>
                              Offshore (Annual)
                            </div>
                          </th>
                          <th style={{
                            padding: '2rem 1.5rem 1.5rem 1.5rem',
                            textAlign: 'center',
                            fontWeight: '700',
                            fontSize: '0.9rem',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                            color: 'white',
                            border: 'none',
                            minWidth: '150px'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.5rem'
                            }}>
                              <i className="bi bi-graph-up-arrow" style={{ fontSize: '1rem' }}></i>
                              Your Savings
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {costData.map((row, index) => (
                          <tr key={index} style={{
                            borderBottom: '1px solid #f1f5f9',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#f8fafc';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}>
                            <td style={{
                              padding: '2rem',
                              fontWeight: '600',
                              color: '#1e293b',
                              fontSize: '1.1rem',
                              border: 'none',
                              borderLeft: '4px solid transparent',
                              transition: 'all 0.3s ease'
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem'
                              }}>
                                <div style={{
                                  width: '8px',
                                  height: '8px',
                                  borderRadius: '50%',
                                  background: 'linear-gradient(135deg, #0ea5e9, #0ea5e9)'
                                }}></div>
                                {row.role}
                              </div>
                            </td>
                            <td style={{
                              padding: '2rem 1.5rem',
                              textAlign: 'center',
                              color: '#64748b',
                              fontSize: '1.1rem',
                              border: 'none',
                              fontWeight: '500'
                            }}>
                              <span style={{
                                background: '#f1f5f9',
                                padding: '0.5rem 1rem',
                                borderRadius: '8px',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                color: '#475569'
                              }}>
                                {row.us}
                              </span>
                            </td>
                            <td style={{
                              padding: '2rem 1.5rem',
                              textAlign: 'center',
                              color: '#64748b',
                              fontSize: '1.1rem',
                              border: 'none',
                              fontWeight: '500'
                            }}>
                              <span style={{
                                background: '#f1f5f9',
                                padding: '0.5rem 1rem',
                                borderRadius: '8px',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                color: '#475569'
                              }}>
                                {row.uk}
                              </span>
                            </td>
                            <td style={{
                              padding: '2rem 1.5rem',
                              textAlign: 'center',
                              border: 'none',
                              position: 'relative'
                            }}>
                              <div style={{
                                background: 'linear-gradient(135deg, #e0f2fe, #bae6fd)',
                                border: '2px solid #0ea5e9',
                                borderRadius: '12px',
                                padding: '0.75rem 1.25rem',
                                position: 'relative',
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: '2px',
                                  background: 'linear-gradient(90deg, #0ea5e9, #0284c7)'
                                }}></div>
                                <span style={{
                                  fontWeight: '700',
                                  color: '#1e293b',
                                  fontSize: '1rem',
                                  position: 'relative',
                                  zIndex: 1
                                }}>
                                  {row.offshore}
                                </span>
                              </div>
                            </td>
                            <td style={{
                              padding: '2rem 1.5rem',
                              textAlign: 'center',
                              border: 'none'
                            }}>
                              <span style={{
                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                color: 'white',
                                padding: '0.75rem 1.5rem',
                                borderRadius: '25px',
                                fontSize: '0.85rem',
                                fontWeight: '700',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                                display: 'inline-block'
                              }}>
                                {row.savings}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile Card View */}
                <div className="d-lg-none">
                  <div className="row g-3">
                    {costData.map((row, index) => (
                      <div key={index} className="col-12">
                        <div style={{
                          background: '#ffffff',
                          borderRadius: 'clamp(12px, 3vw, 16px)',
                          padding: 'clamp(1.25rem, 4vw, 1.75rem)',
                          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.05)',
                          border: '2px solid rgba(14, 165, 233, 0.2)',
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 12px 35px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.08)';
                          e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.05)';
                          e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.2)';
                        }}>
                          {/* Role Header */}
                          <div style={{
                            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                            borderRadius: 'clamp(8px, 2vw, 12px)',
                            padding: 'clamp(0.75rem, 2.5vw, 1rem)',
                            marginBottom: 'clamp(1rem, 3vw, 1.5rem)',
                            boxShadow: '0 4px 12px rgba(14, 165, 233, 0.3)',
                            position: 'relative',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: '2px',
                              background: 'linear-gradient(90deg, rgba(255,255,255,0.3), rgba(255,255,255,0.1))'
                            }}></div>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'clamp(0.75rem, 2vw, 1rem)',
                              position: 'relative',
                              zIndex: 1
                            }}>
                              <div style={{
                                width: 'clamp(8px, 2vw, 10px)',
                                height: 'clamp(8px, 2vw, 10px)',
                                borderRadius: '50%',
                                background: 'rgba(255, 255, 255, 0.9)',
                                flexShrink: 0
                              }}></div>
                              <h4 style={{
                                fontSize: 'clamp(1.1rem, 3vw, 1.3rem)',
                                fontWeight: '700',
                                color: 'white',
                                margin: 0,
                                lineHeight: '1.3',
                                textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
                              }}>
                                {row.role}
                              </h4>
                            </div>
                          </div>

                          {/* Cost Comparison Grid */}
                          <div className="row g-2 justify-content-center">
                            {/* US Cost */}
                            <div className="col-6 d-flex justify-content-center">
                              <div style={{
                                background: '#f8fafc',
                                borderRadius: 'clamp(8px, 2vw, 12px)',
                                padding: 'clamp(0.75rem, 2.5vw, 1rem)',
                                textAlign: 'center',
                                border: '1px solid #e2e8f0',
                                width: '100%',
                                maxWidth: '140px'
                              }}>
                                <div style={{
                                  fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
                                  fontWeight: '600',
                                  color: '#64748b',
                                  marginBottom: 'clamp(0.25rem, 1vw, 0.5rem)',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  🇺🇸 US
                                </div>
                                <div style={{
                                  fontSize: 'clamp(1rem, 3vw, 1.2rem)',
                                  fontWeight: '700',
                                  color: '#475569'
                                }}>
                                  {row.us}
                                </div>
                              </div>
                            </div>

                            {/* UK Cost */}
                            <div className="col-6 d-flex justify-content-center">
                              <div style={{
                                background: '#f8fafc',
                                borderRadius: 'clamp(8px, 2vw, 12px)',
                                padding: 'clamp(0.75rem, 2.5vw, 1rem)',
                                textAlign: 'center',
                                border: '1px solid #e2e8f0',
                                width: '100%',
                                maxWidth: '140px'
                              }}>
                                <div style={{
                                  fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
                                  fontWeight: '600',
                                  color: '#64748b',
                                  marginBottom: 'clamp(0.25rem, 1vw, 0.5rem)',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  🇬🇧 UK
                                </div>
                                <div style={{
                                  fontSize: 'clamp(1rem, 3vw, 1.2rem)',
                                  fontWeight: '700',
                                  color: '#475569'
                                }}>
                                  {row.uk}
                                </div>
                              </div>
                            </div>

                            {/* Offshore Cost - Highlighted */}
                            <div className="col-6 d-flex justify-content-center">
                              <div style={{
                                background: 'linear-gradient(135deg, #e0f2fe, #bae6fd)',
                                border: '2px solid #0ea5e9',
                                borderRadius: 'clamp(8px, 2vw, 12px)',
                                padding: 'clamp(0.75rem, 2.5vw, 1rem)',
                                textAlign: 'center',
                                position: 'relative',
                                overflow: 'hidden',
                                width: '100%',
                                maxWidth: '140px'
                              }}>
                                <div style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  height: '2px',
                                  background: 'linear-gradient(90deg, #0ea5e9, #0284c7)'
                                }}></div>
                                <div style={{
                                  fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
                                  fontWeight: '600',
                                  color: '#0ea5e9',
                                  marginBottom: 'clamp(0.25rem, 1vw, 0.5rem)',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  ⭐ Offshore
                                </div>
                                <div style={{
                                  fontSize: 'clamp(1rem, 3vw, 1.2rem)',
                                  fontWeight: '700',
                                  color: '#1e293b',
                                  position: 'relative',
                                  zIndex: 1
                                }}>
                                  {row.offshore}
                                </div>
                              </div>
                            </div>

                            {/* Savings */}
                            <div className="col-6 d-flex justify-content-center">
                              <div style={{
                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                borderRadius: 'clamp(8px, 2vw, 12px)',
                                padding: 'clamp(0.75rem, 2.5vw, 1rem)',
                                textAlign: 'center',
                                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                                width: '100%',
                                maxWidth: '140px'
                              }}>
                                <div style={{
                                  fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
                                  fontWeight: '600',
                                  color: 'rgba(255, 255, 255, 0.9)',
                                  marginBottom: 'clamp(0.25rem, 1vw, 0.5rem)',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  💰 Savings
                                </div>
                                <div style={{
                                  fontSize: 'clamp(1rem, 3vw, 1.2rem)',
                                  fontWeight: '700',
                                  color: 'white'
                                }}>
                                  {row.savings}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="text-center mt-4">
                  <p style={{
                    fontSize: '0.9rem',
                    color: '#64748b',
                    marginBottom: '1rem'
                  }}>
                    <i className="bi bi-info-circle me-2" style={{ color: '#0ea5e9' }}></i>
                    All rates include project management, quality assurance, and ongoing support
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CostComparisonTable; 